import { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Download, Trash2, Upload, FolderOpen, Loader2, Eye, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { useUploadStore, type UploadItem } from '@/stores/uploadStore';
import { DOCUMENT_CATEGORY_LABELS } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import type { Document, DocumentCategory } from '@/types';
import { cn } from '@/lib/utils';

interface DocumentListProps {
  propertyId: string;
}

// Upload-Vorschau Komponente
function UploadingDocumentPreview({ item }: { item: UploadItem }) {
  return (
    <Card className="p-3 flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <Loader2 className="w-5 h-5 text-primary animate-spin flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{item.fileName}</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden max-w-24">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${item.progress}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {item.status === 'processing' ? 'Verarbeite...' : 'Lade hoch...'}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}

// Dokumenten-Vorschau Modal
function DocumentPreviewModal({
  document,
  url,
  onClose,
}: {
  document: Document;
  url: string;
  onClose: () => void;
}) {
  const isPdf = document.mime_type === 'application/pdf' || document.name.toLowerCase().endsWith('.pdf');
  const isImage = document.mime_type?.startsWith('image/') ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(document.name);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex flex-col"
      onClick={onClose}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50">
        <h3 className="text-white font-medium truncate max-w-md">{document.name}</h3>
        <div className="flex items-center gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-white hover:bg-white/10 rounded-full"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="w-5 h-5" />
          </a>
          <button
            className="p-2 text-white hover:bg-white/10 rounded-full"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 flex items-center justify-center p-4 overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {isPdf ? (
          <iframe
            src={`${url}#view=FitH`}
            className="w-full h-full max-w-4xl bg-white rounded-lg"
            title={document.name}
          />
        ) : isImage ? (
          <img
            src={url}
            alt={document.name}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <div className="text-center text-white">
            <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="mb-4">Vorschau nicht verfügbar</p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg"
            >
              <Download className="w-4 h-4" />
              Herunterladen
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export function DocumentList({ propertyId }: DocumentListProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | 'all'>('all');
  const [isDragging, setIsDragging] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [previewDoc, setPreviewDoc] = useState<{ doc: Document; url: string } | null>(null);

  // Globaler Upload Store
  const addUpload = useUploadStore(state => state.addUpload);
  const uploads = useUploadStore(state => state.uploads);

  // Nur Dokument-Uploads für diese Property filtern
  const pendingUploads = uploads.filter(
    u => u.type === 'document' &&
      u.propertyId === propertyId &&
      (u.status === 'pending' || u.status === 'uploading' || u.status === 'processing')
  );

  const isUploading = pendingUploads.length > 0;

  async function fetchDocuments() {
    const { data } = await supabase
      .from('documents')
      .select('*')
      .eq('property_id', propertyId)
      .order('uploaded_at', { ascending: false });
    setDocuments(data || []);
    setIsLoading(false);
  }

  useEffect(() => {
    fetchDocuments();
  }, [propertyId]);

  // Refresh wenn Uploads abgeschlossen
  const prevCompletedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const completed = uploads.filter(
      u => u.type === 'document' && u.propertyId === propertyId && u.status === 'done'
    );

    const newCompleted = completed.filter(u => !prevCompletedRef.current.has(u.id));
    if (newCompleted.length > 0) {
      fetchDocuments();
      newCompleted.forEach(u => prevCompletedRef.current.add(u.id));
    }
  }, [uploads, propertyId]);

  // Upload-Funktion
  const handleUploadFiles = useCallback((files: File[]) => {
    const category = selectedCategory === 'all' ? 'sonstiges' : selectedCategory;

    files.forEach(file => {
      addUpload({
        type: 'document',
        fileName: file.name,
        propertyId,
        category: category as DocumentCategory,
        file,
      });
    });
  }, [addUpload, propertyId, selectedCategory]);

  // Drag & Drop Handler
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleUploadFiles(files);
  }, [handleUploadFiles]);

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleUploadFiles(files);
    e.target.value = '';
  };

  const handlePreview = async (doc: Document) => {
    const { data } = await supabase.storage
      .from('documents')
      .createSignedUrl(doc.storage_path, 3600);

    if (data?.signedUrl) {
      setPreviewDoc({ doc, url: data.signedUrl });
    }
  };

  const handleDownload = async (doc: Document) => {
    const { data } = await supabase.storage
      .from('documents')
      .createSignedUrl(doc.storage_path, 3600);

    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    }
  };

  const handleDelete = async () => {
    if (!documentToDelete) return;

    try {
      await supabase.storage
        .from('documents')
        .remove([documentToDelete.storage_path]);

      await supabase
        .from('documents')
        .delete()
        .eq('id', documentToDelete.id);

      fetchDocuments();
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDocumentToDelete(null);
    }
  };

  // Nach Kategorie filtern
  const filtered = documents.filter(
    d => selectedCategory === 'all' || d.category === selectedCategory
  );

  const grouped = filtered.reduce((acc, doc) => {
    if (!acc[doc.category]) acc[doc.category] = [];
    acc[doc.category].push(doc);
    return acc;
  }, {} as Record<string, Document[]>);

  // Pending auch nach Kategorie filtern
  const filteredPending = pendingUploads.filter(
    p => selectedCategory === 'all' || p.category === selectedCategory
  );

  const categories = Object.keys(DOCUMENT_CATEGORY_LABELS) as DocumentCategory[];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-heading">Dokumente</h3>
        <div className="flex items-center gap-2">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value as DocumentCategory | 'all')}
            className="input text-sm py-2 bg-background border-border rounded-md px-3"
          >
            <option value="all">Alle Kategorien</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{DOCUMENT_CATEGORY_LABELS[cat]}</option>
            ))}
          </select>
          <div className="relative">
            <input
              type="file"
              multiple
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              onChange={handleUpload}
            />
            <Button variant="secondary" size="sm">
              {isUploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Hochladen
            </Button>
          </div>
        </div>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'border-2 border-dashed rounded-lg p-4 text-center transition-colors',
          isDragging ? 'border-primary bg-primary/5' : 'border-border'
        )}
      >
        <p className="text-sm text-muted-foreground">
          {isUploading
            ? `${pendingUploads.length} ${pendingUploads.length === 1 ? 'Dokument wird' : 'Dokumente werden'} hochgeladen...`
            : 'Dokumente hierher ziehen oder oben auf "Hochladen" klicken'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Max. 20MB pro Datei
        </p>
      </div>

      {/* Uploading Documents */}
      {filteredPending.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {filteredPending.map(item => (
            <UploadingDocumentPreview key={item.id} item={item} />
          ))}
        </div>
      )}

      {/* Document Grid */}
      {isLoading ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="p-3 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 bg-muted rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/4" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : Object.keys(grouped).length > 0 ? (
        Object.entries(grouped).map(([category, docs]) => (
          <div key={category}>
            <p className="text-sm text-muted-foreground mb-2">
              {DOCUMENT_CATEGORY_LABELS[category as DocumentCategory]}
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {docs.map(doc => (
                <Card key={doc.id} className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePreview(doc)}
                      className="p-2 hover:bg-muted rounded-md"
                      title="Vorschau"
                    >
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => handleDownload(doc)}
                      className="p-2 hover:bg-muted rounded-md"
                      title="Herunterladen"
                    >
                      <Download className="w-4 h-4 text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => setDocumentToDelete(doc)}
                      className="p-2 hover:bg-muted rounded-md"
                      title="Löschen"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))
      ) : filteredPending.length === 0 ? (
        <Card className="p-8 text-center">
          <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Noch keine Dokumente</p>
        </Card>
      ) : null}

      {/* Document Preview Modal */}
      {previewDoc && (
        <DocumentPreviewModal
          document={previewDoc.doc}
          url={previewDoc.url}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDelete}
        title="Dokument löschen"
        description={`Möchtest du "${documentToDelete?.name}" wirklich löschen?`}
      />
    </div>
  );
}
